import React, { useState } from "react";

const FrontEndWebDevelopment = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn to build
          responsive and interactive front-end web applications using modern
          tools and technologies.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic computer
          literacy and familiarity with using a code editor (e.g., VS Code).
          <p className="font-bold text-sm">Tools Needed:</p> VS Code, web
          browsers (Chrome, Firefox), Node.js, and a local development
          environment.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Front-End Development",
          objective: "Understand the basics of front-end web development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is front-end development?</li>
                <li>Overview of HTML, CSS, and JavaScript.</li>
                <li>Setting up a development environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install VS Code and create a simple HTML file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: HTML Basics",
          objective: "Learn the fundamentals of HTML.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Structure of an HTML document.</li>
                <li>Basic tags: headings, paragraphs, images, links.</li>
                <li>Introduction to semantic HTML.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a simple webpage with HTML tags.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Advanced HTML",
          objective: "Master advanced HTML features.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Forms and input types.</li>
                <li>Tables, lists, and multimedia elements.</li>
                <li>HTML5 semantic elements.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a form with HTML5 validation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: CSS Basics",
          objective: "Understand the fundamentals of CSS.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CSS? Inline, internal, and external styles.</li>
                <li>Selectors, properties, and values.</li>
                <li>Applying colors and fonts.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Style a webpage with basic CSS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: CSS Layouts",
          objective: "Learn to create layouts with CSS.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Box model: margin, padding, border.</li>
                <li>Flexbox basics.</li>
                <li>Positioning elements (static, relative, absolute).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a layout using Flexbox.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Advanced CSS",
          objective: "Master advanced CSS techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>CSS Grid layout.</li>
                <li>Transitions and animations.</li>
                <li>Media queries for responsiveness.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a responsive layout with CSS Grid.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: JavaScript Basics",
          objective: "Learn the fundamentals of JavaScript.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to JavaScript.</li>
                <li>Variables, data types, and operators.</li>
                <li>Functions and event handling.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a JavaScript function to handle a button click.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: JavaScript Control Structures",
          objective: "Understand control structures in JavaScript.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Conditional statements (if-else, switch).</li>
                <li>Loops (for, while).</li>
                <li>Array methods (map, filter, reduce).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a script to filter an array.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: DOM Manipulation",
          objective: "Learn to manipulate the DOM with JavaScript.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is the DOM?</li>
                <li>Selecting and modifying elements.</li>
                <li>Event listeners and dynamic updates.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Change webpage content dynamically with JavaScript.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Introduction to ES6+",
          objective: "Explore modern JavaScript (ES6+).",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Let, const, and arrow functions.</li>
                <li>Destructuring and spread/rest operators.</li>
                <li>Promises and async/await.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Use async/await to fetch data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Introduction to React",
          objective: "Understand the basics of React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is React? Why use it?</li>
                <li>Setting up a React project with Create React App.</li>
                <li>Components and JSX basics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a simple React component.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: React Components and Props",
          objective: "Learn to build and reuse components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Functional vs. class components.</li>
                <li>Passing data with props.</li>
                <li>Component composition.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a reusable component with props.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: React State and Events",
          objective: "Manage state and handle events in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>State management with useState.</li>
                <li>Event handling in React.</li>
                <li>Form handling basics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a counter app with state and events.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: React Routing with React Router",
          objective: "Implement navigation in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to React Router.</li>
                <li>Setting up routes and navigation.</li>
                <li>Passing parameters in routes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a multi-page app with React Router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: API Integration with Fetch",
          objective: "Fetch and display data from APIs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to APIs.</li>
                <li>Using Fetch API in JavaScript.</li>
                <li>Handling JSON data in React.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fetch data from a public API (e.g., JSONPlaceholder).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: State Management with Context API",
          objective: "Manage global state with Context API.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Context API?</li>
                <li>Creating and using context.</li>
                <li>Reducing prop drilling.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a theme switcher with Context API.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Introduction to Redux",
          objective: "Learn state management with Redux.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Redux? Actions, reducers, store.</li>
                <li>Setting up Redux in a React app.</li>
                <li>Basic state management with Redux.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Redux store for a to-do app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Advanced Redux",
          objective: "Master advanced Redux concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Redux Toolkit.</li>
                <li>Async actions with Redux Thunk.</li>
                <li>Connecting Redux to React components.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fetch API data with Redux Thunk.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Responsive Design with CSS",
          objective: "Build responsive web designs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Media queries and breakpoints.</li>
                <li>Fluid layouts and relative units (%, vw, vh).</li>
                <li>Mobile-first design approach.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Make a webpage responsive with media queries.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: CSS Frameworks (Tailwind CSS)",
          objective: "Use Tailwind CSS for rapid development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Tailwind CSS.</li>
                <li>Utility classes and customization.</li>
                <li>Building layouts with Tailwind.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Style a webpage using Tailwind CSS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Web Accessibility (a11y)",
          objective: "Design accessible web applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of web accessibility.</li>
                <li>WCAG guidelines and ARIA roles.</li>
                <li>Testing with screen readers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add accessibility features to a webpage.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Performance Optimization",
          objective: "Optimize front-end performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Minifying CSS and JavaScript.</li>
                <li>Image optimization and lazy loading.</li>
                <li>Using browser DevTools for performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a webpage for performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Final Project – Planning",
          objective: "Plan the final front-end project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., portfolio, to-do app).</li>
                <li>Defining features and design goals.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and wireframe.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Final Project – HTML and CSS Structure",
          objective: "Build the HTML and CSS structure.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the HTML skeleton.</li>
                <li>Applying CSS layouts and styles.</li>
                <li>Ensuring responsiveness.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Develop the project’s HTML and CSS base.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Final Project – JavaScript Functionality",
          objective: "Add JavaScript interactivity.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implementing DOM manipulation.</li>
                <li>Adding event listeners and logic.</li>
                <li>Testing functionality.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add JavaScript features to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – React Integration",
          objective: "Convert the project to React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up a React project.</li>
                <li>Converting HTML/CSS to components.</li>
                <li>Managing state and props.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build the project in React.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – API Integration",
          objective: "Integrate APIs into the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fetching data with Fetch or Axios.</li>
                <li>Displaying dynamic content.</li>
                <li>Handling errors and loading states.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Integrate an API into the React project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – State Management",
          objective: "Implement state management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Context API or Redux.</li>
                <li>Managing global state effectively.</li>
                <li>Optimizing state updates.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add state management to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Optimization and Testing",
          objective: "Optimize and test the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Performance optimization techniques.</li>
                <li>Testing with browser DevTools.</li>
                <li>Ensuring accessibility and responsiveness.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize and test the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the final front-end project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo or report.</li>
                <li>Discussing development process and challenges.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Build responsive web pages with HTML, CSS, and JavaScript.</li>
            <li>Develop interactive applications using React.</li>
            <li>Integrate APIs and manage state effectively.</li>
            <li>Complete a fully functional front-end project.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Portfolio Website:</span> A
              personal portfolio showcasing skills and projects.
            </li>
            <li>
              <span className="font-bold text-sm">To-Do App:</span> A task
              management app with CRUD functionality.
            </li>
            <li>
              <span className="font-bold text-sm">Weather App:</span> An app
              fetching weather data from an API.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Encourage practice with real-world web projects.</li>
            <li>Provide resources (e.g., MDN Web Docs, React tutorials).</li>
            <li>Use discussion time for debugging and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from web development basics to a complete
            front-end project over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-green-400 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Front-End Web Development Syllabus
        </h1>
        <p className="text-sm text-white">
          Master Front-End Development (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrontEndWebDevelopment;
